import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import { useRootStore } from '../../../../providers/root-store-provider';
import { DropdownMenu } from '../../dropdown/menu/dropdown-menu';
import { DropdownMenuItem } from '../../dropdown/menu/item/dropdown-menu-item';
import { routes } from '../../../../config/routes';
import { IconSettings } from '../../icons/settings';
import { getPortfolios } from '../../../../utils/get-portfolios';
import { AssetPortfolioSimpleRegular } from '../../asset/portfolio/simple/regular/asset-portfolio-simple-regular';
import { AssetPortfolioSimpleSummary } from '../../asset/portfolio/simple/summary/asset-portfolio-simple-summary';
import { Headline } from '../../headline/headline';
import { PortfolioExpandExcluded } from '../expand-excluded/portfolio-expand-excluded';

export const PortfolioMenu: React.FC = observer(() => {
    const { t } = useTranslation();
    const { portfolioStore, statisticStore } = useRootStore();

    const router = useRouter();
    const isIndex = router.pathname === routes.app.index;

    const { summary, activePortfolios, excludedPortfolios } = getPortfolios();

    const canShowSummary = portfolioStore.portfolios.length > 2;
    const [excludedExpanded, setExcludedExpanded] = useState(false);

    useEffect(() => {
        portfolioStore.fetchPinnedPortfolios();
    }, []);

    useEffect(() => {
        if (portfolioStore.isPortfolioListStale) {
            portfolioStore.fetchPortfolioList();
        }
    }, [portfolioStore.isPortfolioListStale]);

    return (
        <DropdownMenu>
            <>
                <li className="flex flex-col gap-[0.6rem] pb-[2rem]">
                    {activePortfolios.map((row) => (
                        <AssetPortfolioSimpleRegular
                            key={row.id}
                            data={row}
                            showAllAssets={true}
                            onClick={async () => {
                                await portfolioStore.selectPortfolio(row.id);
                                if (isIndex) {
                                    await statisticStore.profitStore.fetchProfitCharts();
                                }
                            }}
                        />
                    ))}

                    {summary && canShowSummary ? (
                        <AssetPortfolioSimpleSummary
                            data={summary}
                            onClick={async () => {
                                await portfolioStore.selectPortfolio(summary.id);
                                if (isIndex) {
                                    await statisticStore.profitStore.fetchProfitCharts();
                                }
                            }}
                        />
                    ) : null}

                    {excludedPortfolios.length > 0 ? (
                        <>
                            <PortfolioExpandExcluded
                                excludedExpanded={excludedExpanded}
                                setExcludedExpanded={setExcludedExpanded}
                                excludedPortfolios={excludedPortfolios.length}
                            />
                            {excludedExpanded
                                ? excludedPortfolios.map((row) => (
                                      <AssetPortfolioSimpleRegular
                                          key={row.id}
                                          data={row}
                                          showAllAssets={true}
                                          onClick={async () => {
                                              await portfolioStore.selectPortfolio(row.id);
                                              if (isIndex) {
                                                  await statisticStore.profitStore.fetchProfitCharts();
                                              }
                                          }}
                                      />
                                  ))
                                : null}
                        </>
                    ) : null}
                </li>
                {portfolioStore.pinnedPortfolios.length > 0 ? (
                    <li className="mb-[1rem] flex flex-col gap-[0.6rem] rounded-large border border-border p-vsmall shadow-small [&+li]:border-t-0">
                        <Headline level={5} marginTop={'none'} gap={'xsmall'} variant={'default'}>
                            {t('pages.portfolios.pinnedPortfolios')}
                        </Headline>
                        {portfolioStore.pinnedPortfolios.map((row) => (
                            <AssetPortfolioSimpleRegular
                                key={row.id}
                                data={row}
                                onClick={async () => {
                                    await router.push(`${routes.app.sharedPortfolio}/${row.shareSlug}`);
                                }}
                            />
                        ))}
                    </li>
                ) : null}

                <DropdownMenuItem
                    url={routes.app.portfolios}
                    icon={<IconSettings />}
                    name={t('layout.portfolioMenu.portfolioManagement')}
                />
            </>
        </DropdownMenu>
    );
});
